import React, { useCallback, useEffect, useMemo, useState } from "react";
import BlockContentArrayView from "./BlockContentArrayView";
import type { ContentBlock } from "./BlockContentArrayView";
import type { PickedImage } from "../../User/adding-user/ImageUploadContainer";

export type BlockContentArrayContainerProps = {
  blocks?: Record<string, unknown>[];
  reset?: boolean;
  isRequired?: boolean;
  data?: unknown;
  add?: (blocks: ContentBlock[]) => void;
  delete?: (blocks: ContentBlock[]) => void;
  handleBlockArray?: (blocks: ContentBlock[]) => void;
};

const buildIndexed = (blocks: Record<string, unknown>[] = []): ContentBlock[] =>
  blocks.map((blockObj, idx) => ({ ...blockObj, index: idx }));

const BlockContentArrayContainer = ({
  blocks = [],
  reset = false,
  isRequired = false,
  data,
  add,
  delete: deleteCb,
  handleBlockArray,
}: BlockContentArrayContainerProps) => {
  const [blockContentArray, setBlockContentArray] = useState(buildIndexed(blocks));

  // sync when blocks prop changes
  useEffect(() => {
    setBlockContentArray(buildIndexed(blocks));
  }, [blocks]);

  // reset back to one row
  useEffect(() => {
    if (reset) {
      setBlockContentArray([{ index: 0, title: "", description: "", image: "" }]);
    }
  }, [reset]);

  const addRow = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const newIndex = blockContentArray.length;
      const newRow = { index: newIndex, title: "", description: "", image: "" };
      const updated = [...blockContentArray, newRow];
      setBlockContentArray(updated);
      add && add(updated);
    },
    [blockContentArray, add]
  );

  const deleteRow = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (blockContentArray.length > 0) {
        const updated = blockContentArray.slice(0, -1);
        setBlockContentArray(updated);
        deleteCb && deleteCb(updated);
      }
    },
    [blockContentArray, deleteCb]
  );

  const handleBlock = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, block: ContentBlock) => {
      const { name, value } = e.target;
      const updated = blockContentArray.map((b) =>
        b.index === block.index ? { ...b, [name]: value } : b
      );
      setBlockContentArray(updated);
      handleBlockArray && handleBlockArray(updated);
    },
    [blockContentArray, handleBlockArray]
  );

  const imageUploadReceivedHandler = useCallback(
    (img: PickedImage, blockIndex: number) => {
      const updated = blockContentArray.map((b) =>
        b.index === blockIndex ? { ...b, image: img } : b
      );
      setBlockContentArray(updated);
      handleBlockArray && handleBlockArray(updated);
    },
    [blockContentArray, handleBlockArray]
  );

  const removeImageHandler = useCallback(
    (blockIndex: number) => {
      const updated = blockContentArray.map((b) =>
        b.index === blockIndex ? { ...b, image: "" } : b
      );
      setBlockContentArray(updated);
      handleBlockArray && handleBlockArray(updated);
    },
    [blockContentArray, handleBlockArray]
  );

  const count = useMemo(() => blockContentArray.length, [blockContentArray]);

  return (
    <BlockContentArrayView
      blockContentArray={blockContentArray}
      count={count}
      reset={reset}
      isRequired={isRequired}
      addRow={addRow}
      deleteRow={deleteRow}
      handleBlock={handleBlock}
      imageUploadReceivedHandler={imageUploadReceivedHandler}
      removeImageHandler={removeImageHandler}
    />
  );
};

export default BlockContentArrayContainer;
